import React, { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { serverLink, projectURL } from "../../../resources/url";
import DataLoader from "../../common/dataLoader/dataLoader";
import { formatDateAndTime, currencyConverter } from "../../../resources/constants";
import logo from "../../../images/logo.png";

/* ─── helpers ────────────────────────────────────────────────────────────── */
const fmt = (amount) =>
    currencyConverter
        ? currencyConverter(amount)
        : Number(amount).toLocaleString("en-NG", { style: "currency", currency: "NGN" });

const groupCourses = (courses) => {
    const map = {};
    (courses || []).forEach((c) => {
        const key = `Level ${c.LEVEL} — Semester ${c.SEMESTER}`;
        if (!map[key]) map[key] = [];
        map[key].push(c);
    });
    return map;
};

/* ─── SessionSelect ─────────────────────────────────────────────────────── */
function SessionSelect({ currentSession, sessions, onSave, saving }) {
    const [val, setVal] = useState(currentSession || "");
    const changed = val !== (currentSession || "");
    return (
        <div className="d-flex align-items-center gap-1" style={{ minWidth: "200px" }}>
            <select
                className="form-select form-select-sm"
                value={val}
                onChange={(e) => setVal(e.target.value)}
                style={{ fontSize: "11px" }}
            >
                <option value="">— select session —</option>
                {sessions.map((s, i) => (
                    <option key={i} value={s.Session}>
                        {s.Session}{s.Status === 1 || s.Status === "1" ? " ✓" : ""}
                    </option>
                ))}
            </select>
            {changed && (
                <button
                    className="btn btn-sm btn-success py-0 px-2"
                    style={{ fontSize: "11px", whiteSpace: "nowrap" }}
                    onClick={() => onSave(val)}
                    disabled={saving || !val}
                >
                    {saving ? <span className="spinner-border spinner-border-sm" /> : "Save"}
                </button>
            )}
        </div>
    );
}

/* ─── ExamCard print layout ─────────────────────────────────────────────── */
function ExamCardPrint({ bio, cardCourses, examSession, examSemester }) {
    const studentName = [bio.FIRST_NAME, bio.MIDDLE_NAME, bio.LAST_NAME].filter(Boolean).join(" ");
    const semesterLabel = examSemester === "1" ? "First" : "Second";
    const rawLevel = cardCourses?.[0]?.LEVEL ?? bio.STUDENT_LEVEL ?? "";
    const levelLabel = rawLevel ? `${rawLevel}00 Level` : "—";

    return (
        <div id="exam-card-print-area" style={{ fontFamily: "Arial, sans-serif" }}>
            <style>{`
                @media print {
                    body * { visibility: hidden !important; }
                    #exam-card-print-area,
                    #exam-card-print-area * { visibility: visible !important; }
                    #exam-card-print-area {
                        position: fixed !important;
                        top: 0 !important;
                        left: 0 !important;
                        width: 100% !important;
                        z-index: 9999 !important;
                        background: white !important;
                        padding: 20px !important;
                    }
                }
            `}</style>

            <div style={{
                border: "2px solid black",
                padding: "16px",
                maxWidth: "680px",
                margin: "0 auto",
                position: "relative",
                overflow: "hidden",
                background: "#fff",
            }}>
                {/* Watermark */}
                <div style={{
                    position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "70px", fontWeight: "bold", color: "#000",
                    opacity: 0.06, transform: "rotate(-45deg)", pointerEvents: "none",
                    zIndex: 0,
                }}>
                    OFFICIAL
                </div>

                {/* Header */}
                <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", position: "relative", zIndex: 1 }}>
                    <div style={{ minWidth: "90px", textAlign: "center" }}>
                        <img src={logo} alt="Kadpoly ODFeL" style={{ width: "90px", height: "80px", objectFit: "contain" }} />
                    </div>
                    <div style={{ flex: 1, textAlign: "center" }}>
                        <h2 style={{ fontSize: "18px", fontWeight: "bold", margin: "0 0 2px" }}>
                            Kaduna Polytechnic
                        </h2>
                        <h6 style={{ fontWeight: "bold", margin: "0 0 2px", fontSize: "12px" }}>
                            Open, Distance and Flexible e-Learning (ODFeL) Centre
                        </h6>
                        <h5 style={{ fontWeight: "bold", margin: "0 0 2px", fontSize: "13px" }}>
                            EXAMINATION CARD
                        </h5>
                        <h5 style={{ fontWeight: "bold", fontSize: "12px", margin: 0, textTransform: "uppercase" }}>
                            DEPARTMENT OF {bio.DEPARTMENT || bio.PROGRAMME || "—"}
                        </h5>
                    </div>
                    <div style={{ minWidth: "90px", textAlign: "center" }}>
                        {bio.PICTURE ? (
                            <img
                                src={`${projectURL}assets/passport/${bio.PICTURE}`}
                                alt="Passport"
                                style={{ width: "90px", height: "100px", objectFit: "cover", border: "2px solid #ccc" }}
                                onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                            />
                        ) : null}
                        <div style={{
                            width: "90px", height: "100px", border: "2px solid #ccc",
                            display: bio.PICTURE ? "none" : "flex",
                            alignItems: "center", justifyContent: "center",
                            background: "#f8f9fa", fontSize: "11px", color: "#888",
                        }}>
                            PASSPORT
                        </div>
                    </div>
                </div>

                {/* Student Info Table */}
                <table style={{ width: "100%", fontSize: "12px", marginTop: "16px", borderCollapse: "collapse" }}>
                    <tbody>
                        {[
                            ["Full Name:", studentName.toUpperCase()],
                            ["Registration No.:", bio.STUDENTID || "—"],
                            ["Level:", levelLabel],
                            ["Semester:", semesterLabel],
                            ["Session:", examSession],
                        ].map(([label, value], i) => (
                            <tr key={i}>
                                <th style={{ width: "160px", padding: "3px 8px 3px 0", textAlign: "left" }}>{label}</th>
                                <td style={{ padding: "3px 0", textTransform: "uppercase" }}>{value}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <hr style={{ margin: "10px 0", borderColor: "#000" }} />

                {/* Courses */}
                <h6 style={{ textAlign: "center", fontWeight: "bold", fontSize: "13px", margin: "8px 0" }}>
                    Registered Course(s)
                </h6>
                <table style={{
                    width: "100%", borderCollapse: "collapse", fontSize: "11px",
                    border: "1px solid black",
                }}>
                    <thead>
                        <tr style={{ background: "#e9ecef" }}>
                            {["S/N", "Course Code", "Course Title", "Seat No"].map((h, i) => (
                                <th key={i} style={{ border: "1px solid black", padding: "5px 8px", textAlign: "left" }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {cardCourses.map((c, i) => (
                            <tr key={i}>
                                <td style={{ border: "1px solid black", padding: "4px 8px" }}>{i + 1}</td>
                                <td style={{ border: "1px solid black", padding: "4px 8px" }}>{c.COURSE_CODE}</td>
                                <td style={{ border: "1px solid black", padding: "4px 8px" }}>{c.COURSE_TITLE}</td>
                                <td style={{ border: "1px solid black", padding: "4px 8px" }}>{c.SeatNo || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* Signature area */}
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: "32px", fontSize: "12px" }}>
                    <div style={{ textAlign: "center" }}>
                        <div style={{ borderTop: "1px solid black", width: "160px", paddingTop: "4px" }}>Student's Signature</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                        <div style={{ borderTop: "1px solid black", width: "160px", paddingTop: "4px" }}>Invigilator's Signature</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ─── main component ─────────────────────────────────────────────────────── */
function StudentPaymentReport(props) {
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [noResults, setNoResults] = useState(false);

    const [student, setStudent] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    const [sessions, setSessions] = useState([]);

    const [activeTab, setActiveTab] = useState("payments");

    const [savingPayment, setSavingPayment] = useState({});
    const [savingCourse, setSavingCourse] = useState({});

    // Exam card state
    const [examSession, setExamSession] = useState("");
    const [examSemester, setExamSemester] = useState("");
    const [examLevel, setExamLevel] = useState("");
    const [cardCourses, setCardCourses] = useState(null); // null = not generated
    const [loadingCard, setLoadingCard] = useState(false);
    const [cardError, setCardError] = useState("");

    const debounceRef = useRef(null);

    /* ── load sessions on mount ── */
    useEffect(() => {
        axios.get(`${serverLink}staff/report/student/payment-report/sessions`)
            .then(({ data }) => setSessions(data))
            .catch((e) => console.error("Sessions load error", e));
    }, []);

    /* ── reset exam card when student changes ── */
    useEffect(() => {
        setCardCourses(null);
        setCardError("");
        setExamSession("");
        setExamSemester("");
        setExamLevel("");
    }, [student]);

    /* ── search ── */
    const handleSearchInput = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        setStudent(null);
        setSearchResults([]);
        setNoResults(false);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (val.trim().length < 2) return;
        debounceRef.current = setTimeout(() => runSearch(val.trim()), 400);
    };

    const runSearch = async (term) => {
        setSearching(true);
        try {
            const { data } = await axios.get(
                `${serverLink}staff/report/student/payment-report/search/${encodeURIComponent(term)}`
            );
            setSearchResults(data);
            setNoResults(data.length === 0);
        } catch (err) {
            console.error("Search error", err);
        } finally {
            setSearching(false);
        }
    };

    /* ── select student ── */
    const selectStudent = async (studentid) => {
        setSearchResults([]);
        setSearchTerm("");
        setLoadingDetail(true);
        setStudent(null);
        setActiveTab("payments");
        try {
            const { data } = await axios.get(
                `${serverLink}staff/report/student/payment-report/detail/${encodeURIComponent(studentid)}`
            );
            setStudent(data);
        } catch (err) {
            console.error("Detail error", err);
        } finally {
            setLoadingDetail(false);
        }
    };

    /* ── update payment session ── */
    const updatePaymentSession = async (transactionId, sessionCode) => {
        setSavingPayment((prev) => ({ ...prev, [transactionId]: true }));
        try {
            await axios.patch(`${serverLink}staff/report/student/payment-report/update-payment-session`, {
                TRANSACTION_ID: transactionId,
                SESSION_CODE: sessionCode,
            });
            setStudent((prev) => ({
                ...prev,
                Payments: prev.Payments.map((p) =>
                    p.TRANSACTION_ID === transactionId ? { ...p, SESSION_CODE: sessionCode } : p
                ),
            }));
        } catch (err) {
            console.error("Payment session update error", err);
            alert("Failed to update payment session. Please try again.");
        } finally {
            setSavingPayment((prev) => ({ ...prev, [transactionId]: false }));
        }
    };

    /* ── update course session ── */
    const updateCourseSession = async (entryId, sessionCode) => {
        setSavingCourse((prev) => ({ ...prev, [entryId]: true }));
        try {
            await axios.patch(`${serverLink}staff/report/student/payment-report/update-course-session`, {
                ENTRYID: entryId,
                SESSION_CODE: sessionCode,
            });
            setStudent((prev) => ({
                ...prev,
                Courses: prev.Courses.map((c) =>
                    c.ENTRYID === entryId ? { ...c, SESSION_CODE: sessionCode } : c
                ),
            }));
        } catch (err) {
            console.error("Course session update error", err);
            alert("Failed to update course session. Please try again.");
        } finally {
            setSavingCourse((prev) => ({ ...prev, [entryId]: false }));
        }
    };

    /* ── generate exam card ── */
    const generateExamCard = async () => {
        if (!examSession || !examSemester) return;
        const studentId = bio?.STUDENTID;
        if (!studentId) return;
        setLoadingCard(true);
        setCardCourses(null);
        setCardError("");
        try {
            const { data } = await axios.get(
                `${serverLink}staff/report/student/payment-report/exam-card`, {
                params: {
                    STUDENTID: studentId,
                    SESSION: examSession,
                    SEMESTER: examSemester,
                    ...(examLevel ? { LEVEL: examLevel } : {}),
                }
            }
            );
            if (data.length === 0) {
                setCardError(
                    `No courses found for ${studentId} in ${examSession} — ${examSemester === "1" ? "First" : "Second"} Semester. ` +
                    `Kindly ensure the student has paid the Management Fee and registered courses for this session.`
                );
            } else {
                setCardCourses(data);
            }
        } catch (err) {
            console.error("Exam card error", err);
            setCardError("Failed to generate exam card. Please try again.");
        } finally {
            setLoadingCard(false);
        }
    };

    /* ── derived ── */
    const bio = student?.Biodata?.[0] ?? null;
    const payments = student?.Payments ?? [];
    const courses = student?.Courses ?? [];
    const grouped = groupCourses(courses);
    const totalPaid = payments.reduce((s, p) => s + (parseFloat(p.AMOUNT) || 0), 0);

    /* ════════════════════════════════════════════════════════════════════════ */
    return (
        <>
            {/* PAGE HEADER */}
            <div className="container-xl">
                <div className="page-header d-print-none">
                    <div className="row align-items-center">
                        <div className="col">
                            <div className="page-pretitle">Reports</div>
                            <h2 className="page-title d-flex align-items-center gap-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="icon text-primary" width="24"
                                    height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"
                                    fill="none" strokeLinecap="round" strokeLinejoin="round">
                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                    <circle cx="10" cy="10" r="7" />
                                    <line x1="21" y1="21" x2="15" y2="15" />
                                </svg>
                                Student Payment Report
                            </h2>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-xl">
                    <div className="row row-deck row-cards">

                        {/* ── SEARCH CARD ── */}
                        <div className="col-12 d-print-none">
                            <div className="card">
                                <div className="card-header">
                                    <h3 className="card-title">Search Student</h3>
                                </div>
                                <div className="card-body">
                                    <div className="row g-2">
                                        <div className="col-md-6 position-relative">
                                            <div className="input-group">
                                                <span className="input-group-text">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                        viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"
                                                        fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                        <circle cx="10" cy="10" r="7" />
                                                        <line x1="21" y1="21" x2="15" y2="15" />
                                                    </svg>
                                                </span>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Search by name, email, phone or student ID…"
                                                    value={searchTerm}
                                                    onChange={handleSearchInput}
                                                    autoComplete="off"
                                                    id="student-search-input"
                                                />
                                                {searching && (
                                                    <span className="input-group-text">
                                                        <span className="spinner-border spinner-border-sm" role="status" />
                                                    </span>
                                                )}
                                            </div>
                                            <div className="form-hint mt-1">
                                                Type at least 2 characters — name, email, phone or student ID
                                            </div>

                                            {(searchResults.length > 0 || noResults) && (
                                                <div className="card shadow" style={{
                                                    position: "absolute", top: "calc(100% + 4px)",
                                                    left: 0, right: 0, zIndex: 1000,
                                                    maxHeight: "320px", overflowY: "auto",
                                                    border: "1px solid #dee2e6",
                                                }}>
                                                    {noResults ? (
                                                        <div className="px-3 py-3 text-muted text-center" style={{ fontSize: "13px" }}>
                                                            No student found for "<strong>{searchTerm}</strong>"
                                                        </div>
                                                    ) : (
                                                        <div className="list-group list-group-flush">
                                                            {searchResults.map((s, i) => (
                                                                <button key={i}
                                                                    className="list-group-item list-group-item-action d-flex align-items-start gap-2 py-2"
                                                                    onClick={() => selectStudent(s.STUDENTID)}
                                                                    style={{ cursor: "pointer", fontSize: "13px" }}>
                                                                    <span className={`avatar avatar-xs mt-1 ${s.GENDER === "Female" ? "bg-pink-lt" : "bg-azure-lt"}`}
                                                                        style={{ fontSize: "10px", minWidth: "24px" }}>
                                                                        {s.STUDENT_NAME?.charAt(0) ?? "?"}
                                                                    </span>
                                                                    <div>
                                                                        <div className="fw-semibold">{s.STUDENT_NAME}</div>
                                                                        <div className="text-muted" style={{ fontSize: "11px" }}>
                                                                            {s.STUDENTID || s.APPID} &bull; {s.EMAIL} &bull; {s.TELEPHONE}
                                                                        </div>
                                                                        <span className="badge bg-blue-lt" style={{ fontSize: "11px" }}>{s.PROGRAMME}</span>
                                                                    </div>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── LOADING ── */}
                        {loadingDetail && (
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-body d-flex justify-content-center align-items-center py-5">
                                        <DataLoader />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── STUDENT DETAIL ── */}
                        {!loadingDetail && bio && (
                            <>
                                {/* Student info card */}
                                <div className="col-12 d-print-none">
                                    <div className="card" style={{ borderTop: "3px solid #206bc4" }}>
                                        <div className="card-body">
                                            <div className="row align-items-center g-3">
                                                <div className="col-auto">
                                                    {bio.PICTURE ? (
                                                        <img
                                                            src={`${projectURL}assets/passport/${bio.PICTURE}`}
                                                            alt={bio.FIRST_NAME}
                                                            style={{
                                                                width: "60px", height: "60px",
                                                                borderRadius: "50%", objectFit: "cover",
                                                                border: "2px solid #206bc4",
                                                            }}
                                                            onError={(e) => {
                                                                e.target.style.display = "none";
                                                                e.target.nextSibling.style.display = "inline-flex";
                                                            }}
                                                        />
                                                    ) : null}
                                                    <span
                                                        className={`avatar avatar-lg ${bio.GENDER === "Female" ? "bg-pink-lt text-pink" : "bg-azure-lt text-azure"}`}
                                                        style={{
                                                            fontSize: "22px", fontWeight: 700,
                                                            width: "60px", height: "60px",
                                                            display: bio.PICTURE ? "none" : "inline-flex",
                                                        }}
                                                    >
                                                        {`${bio.FIRST_NAME ?? ""}`.charAt(0)}
                                                    </span>
                                                </div>
                                                <div className="col">
                                                    <h3 className="mb-0">
                                                        {[bio.FIRST_NAME, bio.MIDDLE_NAME, bio.LAST_NAME].filter(Boolean).join(" ")}
                                                    </h3>
                                                    <div className="text-muted" style={{ fontSize: "13px" }}>
                                                        {bio.STUDENTID || bio.APPID} &nbsp;&bull;&nbsp; {bio.EMAIL} &nbsp;&bull;&nbsp; {bio.TELEPHONE}
                                                    </div>
                                                </div>
                                                <div className="col-auto d-flex gap-2 flex-wrap">
                                                    <div className="card card-sm mb-0" style={{ border: "1px solid #206bc4", minWidth: "120px" }}>
                                                        <div className="card-body py-2 px-3">
                                                            <div className="text-muted" style={{ fontSize: "10px", textTransform: "uppercase" }}>Total Paid</div>
                                                            <div style={{ fontWeight: 700, color: "#206bc4", fontSize: "16px" }}>{fmt(totalPaid)}</div>
                                                        </div>
                                                    </div>
                                                    <div className="card card-sm mb-0" style={{ border: "1px solid #2fb344", minWidth: "100px" }}>
                                                        <div className="card-body py-2 px-3">
                                                            <div className="text-muted" style={{ fontSize: "10px", textTransform: "uppercase" }}>Payments</div>
                                                            <div style={{ fontWeight: 700, color: "#2fb344", fontSize: "16px" }}>{payments.length}</div>
                                                        </div>
                                                    </div>
                                                    <div className="card card-sm mb-0" style={{ border: "1px solid #f59f00", minWidth: "100px" }}>
                                                        <div className="card-body py-2 px-3">
                                                            <div className="text-muted" style={{ fontSize: "10px", textTransform: "uppercase" }}>Courses</div>
                                                            <div style={{ fontWeight: 700, color: "#f59f00", fontSize: "16px" }}>{courses.length}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row g-2 mt-3" style={{ fontSize: "13px" }}>
                                                {[
                                                    ["Programme",       bio.PROGRAMME],
                                                    ["Department",      bio.DEPARTMENT || "—"],
                                                    ["Gender",          bio.GENDER],
                                                    ["Nationality",     bio.NATIONALITY],
                                                    ["State of Origin", bio.STATE_OF_ORIGIN],
                                                    ["Date of Birth",   bio.DOB ? formatDateAndTime(bio.DOB, "date") : "—"],
                                                    ["Admission Year",  bio.ADMISSION_YEAR],
                                                    ["App Status",      bio.APP_STATUS],
                                                ].map(([label, value], i) => (
                                                    <div key={i} className="col-6 col-md-3">
                                                        <div className="text-muted" style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
                                                        <div className="fw-medium">{value || "—"}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* ── TABS ── */}
                                <div className="col-12">
                                    <div className="card">
                                        {/* Tab nav */}
                                        <div className="card-header d-print-none" style={{ padding: "0 16px" }}>
                                            <ul className="nav nav-tabs card-header-tabs" role="tablist">
                                                <li className="nav-item">
                                                    <button className={`nav-link ${activeTab === "payments" ? "active" : ""}`}
                                                        onClick={() => setActiveTab("payments")} id="tab-payments">
                                                        💳 Payments
                                                        <span className="badge bg-blue ms-1">{payments.length}</span>
                                                    </button>
                                                </li>
                                                <li className="nav-item">
                                                    <button className={`nav-link ${activeTab === "courses" ? "active" : ""}`}
                                                        onClick={() => setActiveTab("courses")} id="tab-courses">
                                                        📚 Registered Courses
                                                        <span className="badge bg-yellow ms-1">{courses.length}</span>
                                                    </button>
                                                </li>
                                                <li className="nav-item">
                                                    <button className={`nav-link ${activeTab === "examcard" ? "active" : ""}`}
                                                        onClick={() => setActiveTab("examcard")} id="tab-examcard">
                                                        🎓 Exam Card
                                                    </button>
                                                </li>
                                            </ul>
                                        </div>

                                        {/* ══ PAYMENTS TAB ══ */}
                                        {activeTab === "payments" && (
                                            <div className="card-body p-0">
                                                {payments.length === 0 ? (
                                                    <div className="empty py-5">
                                                        <p className="empty-title">No payment records</p>
                                                        <p className="empty-subtitle text-muted">This student has no active payment records.</p>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="px-3 py-2 d-flex align-items-center gap-3"
                                                            style={{ background: "#f0f4ff", borderBottom: "1px solid #dee2e6" }}>
                                                            <span style={{ fontSize: "13px", color: "#206bc4", fontWeight: 600 }}>
                                                                Grand Total: {fmt(totalPaid)}
                                                            </span>
                                                            <span className="text-muted" style={{ fontSize: "12px" }}>
                                                                ({payments.length} transaction{payments.length !== 1 ? "s" : ""})
                                                            </span>
                                                            <span className="ms-auto text-muted" style={{ fontSize: "11px" }}>
                                                                Change the Session dropdown on any row then click Save
                                                            </span>
                                                        </div>
                                                        <div className="table-responsive">
                                                            <table className="table table-sm table-hover table-vcenter mb-0">
                                                                <thead>
                                                                    <tr style={{ background: "#f8f9fa" }}>
                                                                        {["#", "Transaction ID", "Payment Type", "Pay Code", "Amount", "Reference", "Session", "Date Paid"].map((h, i) => (
                                                                            <th key={i} className="text-xs" style={{ fontWeight: 600, fontSize: "11px" }}>{h}</th>
                                                                        ))}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {payments.map((p, idx) => (
                                                                        <tr key={idx}>
                                                                            <td className="text-muted w-1">{idx + 1}</td>
                                                                            <td className="text-xs font-weight-bold">{p.TRANSACTION_ID}</td>
                                                                            <td className="text-xs">{p.PAYMENT_TYPE}</td>
                                                                            <td className="text-xs"><span className="badge bg-blue-lt">{p.PAYCODE}</span></td>
                                                                            <td className="text-xs fw-semibold" style={{ color: "#2fb344" }}>{fmt(p.AMOUNT)}</td>
                                                                            <td className="text-xs">{p.REFERENCE}</td>
                                                                            <td className="text-xs">
                                                                                <SessionSelect
                                                                                    currentSession={p.SESSION_CODE}
                                                                                    sessions={sessions}
                                                                                    onSave={(newSession) => updatePaymentSession(p.TRANSACTION_ID, newSession)}
                                                                                    saving={!!savingPayment[p.TRANSACTION_ID]}
                                                                                />
                                                                            </td>
                                                                            <td className="text-xs text-muted">{p.DATE_PAID}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                                <tfoot>
                                                                    <tr style={{ background: "#e8f5e9", fontWeight: 700 }}>
                                                                        <td colSpan={4} className="text-xs" style={{ fontWeight: 700 }}>
                                                                            Total ({payments.length} payment{payments.length !== 1 ? "s" : ""})
                                                                        </td>
                                                                        <td className="text-xs" style={{ color: "#155724", fontWeight: 700 }}>{fmt(totalPaid)}</td>
                                                                        <td colSpan={3} />
                                                                    </tr>
                                                                </tfoot>
                                                            </table>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {/* ══ COURSES TAB ══ */}
                                        {activeTab === "courses" && (
                                            <div className="card-body">
                                                <div className="alert alert-info alert-sm py-2 px-3 mb-3" style={{ fontSize: "12px" }}>
                                                    🔒 To update a course session, change the <strong>Session</strong> dropdown on any row and click <strong>Save</strong>.
                                                </div>
                                                {courses.length === 0 ? (
                                                    <div className="empty py-5">
                                                        <p className="empty-title">No registered courses</p>
                                                        <p className="empty-subtitle text-muted">This student has not registered any courses yet.</p>
                                                    </div>
                                                ) : (
                                                    Object.entries(grouped).map(([group, items], gi) => (
                                                        <div key={gi} className="mb-4">
                                                            <div className="d-flex align-items-center gap-2 mb-2">
                                                                <span className="badge bg-indigo text-white" style={{ fontSize: "12px" }}>{group}</span>
                                                                <span className="text-muted" style={{ fontSize: "12px" }}>{items.length} course{items.length !== 1 ? "s" : ""}</span>
                                                            </div>
                                                            <div className="table-responsive">
                                                                <table className="table table-sm table-bordered table-hover mb-0">
                                                                    <thead>
                                                                        <tr style={{ background: "#f8f9fa" }}>
                                                                            {["#", "Course Code", "Session", "Level", "Semester", "Status", "Date Registered"].map((h, i) => (
                                                                                <th key={i} className="text-xs" style={{ fontWeight: 600, fontSize: "11px" }}>{h}</th>
                                                                            ))}
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {items.map((c, ci) => (
                                                                            <tr key={ci}>
                                                                                <td className="text-muted w-1">{ci + 1}</td>
                                                                                <td className="text-xs fw-medium">
                                                                                    <span className="badge bg-azure-lt text-azure">{c.COURSE_CODE}</span>
                                                                                </td>
                                                                                <td className="text-xs">
                                                                                    <SessionSelect
                                                                                        currentSession={c.SESSION_CODE}
                                                                                        sessions={sessions}
                                                                                        onSave={(newSession) => updateCourseSession(c.ENTRYID, newSession)}
                                                                                        saving={!!savingCourse[c.ENTRYID]}
                                                                                    />
                                                                                </td>
                                                                                <td className="text-xs"><span className="badge bg-indigo-lt">{c.LEVEL}</span></td>
                                                                                <td className="text-xs">{c.SEMESTER}</td>
                                                                                <td className="text-xs">
                                                                                    <span className={`badge ${c.COURSE_STATUS === "Active" ? "bg-success-lt text-success" : "bg-danger-lt text-danger"}`}>
                                                                                        {c.COURSE_STATUS}
                                                                                    </span>
                                                                                </td>
                                                                                <td className="text-xs text-muted">{c.DATE_REG || "—"}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        )}

                                        {/* ══ EXAM CARD TAB ══ */}
                                        {activeTab === "examcard" && (
                                            <div className="card-body">
                                                {/* Form to pick session + semester */}
                                                <div className="d-print-none mb-4">
                                                    <div className="row g-3 align-items-end">
                                                        <div className="col-md-4">
                                                            <label className="form-label fw-semibold">Session</label>
                                                            <select
                                                                className="form-select"
                                                                value={examSession}
                                                                onChange={(e) => { setExamSession(e.target.value); setCardCourses(null); setCardError(""); }}
                                                            >
                                                                <option value="">— Select Session —</option>
                                                                {sessions.map((s, i) => (
                                                                    <option key={i} value={s.Session}>
                                                                        {s.Session} ({s.Year}){s.Status === 1 || s.Status === "1" ? " ✓ Active" : ""}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div className="col-md-3">
                                                            <label className="form-label fw-semibold">Semester</label>
                                                            <select
                                                                className="form-select"
                                                                value={examSemester}
                                                                onChange={(e) => { setExamSemester(e.target.value); setCardCourses(null); setCardError(""); }}
                                                            >
                                                                <option value="">— Select Semester —</option>
                                                                <option value="1">First Semester</option>
                                                                <option value="2">Second Semester</option>
                                                            </select>
                                                        </div>
                                                        <div className="col-md-2">
                                                            <label className="form-label fw-semibold">Level <span className="text-muted" style={{ fontSize: "11px" }}>(optional)</span></label>
                                                            <select
                                                                className="form-select"
                                                                value={examLevel}
                                                                onChange={(e) => { setExamLevel(e.target.value); setCardCourses(null); setCardError(""); }}
                                                            >
                                                                <option value="">— All Levels —</option>
                                                                {[1, 2, 3, 4, 5].map(l => (
                                                                    <option key={l} value={l}>{l}00 Level</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div className="col-md-3">
                                                            <button
                                                                className="btn btn-primary w-100"
                                                                onClick={generateExamCard}
                                                                disabled={!examSession || !examSemester || loadingCard}
                                                            >
                                                                {loadingCard
                                                                    ? <><span className="spinner-border spinner-border-sm me-2" />Generating…</>
                                                                    : "🎓 Generate Exam Card"}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Error */}
                                                {cardError && (
                                                    <div className="alert alert-danger d-print-none" style={{ fontSize: "13px" }}>
                                                        {cardError}
                                                    </div>
                                                )}

                                                {/* Exam Card output */}
                                                {cardCourses && (
                                                    <>
                                                        {/* Print button */}
                                                        <div className="d-flex justify-content-end mb-3 d-print-none">
                                                            <button
                                                                className="btn btn-success d-flex align-items-center gap-2"
                                                                onClick={() => window.print()}
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                                                                    viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"
                                                                    fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                                    <path d="M17 17h2a2 2 0 0 0 2 -2v-4a2 2 0 0 0 -2 -2h-14a2 2 0 0 0 -2 2v4a2 2 0 0 0 2 2h2" />
                                                                    <path d="M17 9v-4a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v4" />
                                                                    <rect x="7" y="13" width="10" height="8" rx="2" />
                                                                </svg>
                                                                Print Exam Card
                                                            </button>
                                                        </div>

                                                        <ExamCardPrint
                                                            bio={bio}
                                                            cardCourses={cardCourses}
                                                            examSession={examSession}
                                                            examSemester={examSemester}
                                                        />
                                                    </>
                                                )}

                                                {/* Idle state */}
                                                {!cardCourses && !cardError && !loadingCard && (
                                                    <div className="empty py-4 d-print-none">
                                                        <p className="empty-title">Select session and semester to generate the exam card</p>
                                                        <p className="empty-subtitle text-muted">
                                                            The card will only generate if the student has paid the Management Fee
                                                            and registered courses for the selected session.
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}

                        {/* ── EMPTY STATE ── */}
                        {!loadingDetail && !bio && (
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="empty py-5">
                                            <div className="empty-icon">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-lg text-muted"
                                                    width="48" height="48" viewBox="0 0 24 24" strokeWidth="1.5"
                                                    stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                                    <circle cx="10" cy="10" r="7" />
                                                    <line x1="21" y1="21" x2="15" y2="15" />
                                                </svg>
                                            </div>
                                            <p className="empty-title">Search for a student</p>
                                            <p className="empty-subtitle text-muted">
                                                Type a name, email, phone number or student ID in the search box above.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>
        </>
    );
}

const mapStateToProps = (state) => ({ loginData: state.loginDetails });
export default connect(mapStateToProps, null)(StudentPaymentReport);
